import { format, Locale, setDefaultOptions } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
import i18n from '@translations/i18n';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';

const dateFnsLocales: Record<string, Locale> = {
  nl,
  en: enUS,
};

const localeDateTimeFormat: Record<string, string> = {
  en: 'MMMM d, yyyy',
  nl: 'd MMMM yyyy',
};

const localeDateWithoutYearFormat: Record<string, string> = {
  en: 'MMMM d',
  nl: 'd MMMM',
};

const localeDateShortFormat: Record<string, string> = {
  en: 'd MMM yyyy',
  nl: 'd MMM yyyy',
};

const localeDateTimeWithDayNameFormat: Record<string, string> = {
  en: 'EEEE, MMMM d, yyyy',
  nl: 'EEEE d MMMM yyyy',
};

export const resolveAppLanguage = (): string => {
  const lang = i18n.resolvedLanguage || i18n.language || 'en';

  if (lang.startsWith('nl')) {
    return 'nl';
  }

  if (lang.startsWith('en')) {
    return 'en';
  }

  return 'en';
};

export const getDateFnsLocale = (): Locale => {
  return dateFnsLocales[resolveAppLanguage()] ?? enUS;
};

export const syncDateLibrariesLocale = (): void => {
  const lang = resolveAppLanguage();

  setDefaultOptions({ locale: getDateFnsLocale() });
  dayjs.locale(lang);
};

const parseToDate = (date: Date | string | Dayjs): Date => {
  if (typeof date === 'string') {
    return new Date(date);
  }

  if ('toDate' in date) {
    return date.toDate();
  }

  return date;
};

export const formatLocaleDate = (
  date: Date | string | Dayjs,
  withTime: boolean = true,
  withDayName: boolean = false
) => {
  const lang = resolveAppLanguage();
  let formatStr = withDayName
    ? localeDateTimeWithDayNameFormat[lang]
    : localeDateTimeFormat[lang];

  if (withTime) {
    formatStr += ' HH:mm';
  }

  return format(parseToDate(date), formatStr, { locale: getDateFnsLocale() });
};

export const formatLocaleDateWithoutYear = (date: Date | string | Dayjs) => {
  const formatStr = localeDateWithoutYearFormat[resolveAppLanguage()];

  return format(parseToDate(date), formatStr, { locale: getDateFnsLocale() });
};

export const formatLocaleTime = (date: Date | string | Dayjs): string => {
  return format(parseToDate(date), 'HH:mm', {
    locale: getDateFnsLocale(),
  });
};

const parseCalendarDateString = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  return dayjs(value).toDate();
};

export const formatLocaleDateAtTime = (date: Date | string | Dayjs): string => {
  const parsed =
    typeof date === 'string'
      ? (parseCalendarDateString(date) ??
        (date.includes(' ') || date.includes('T')
          ? dayjs(date).toDate()
          : parseToDate(date)))
      : parseToDate(date);
  const datePart = formatLocaleDate(parsed, false);
  const timePart = formatLocaleTime(parsed);

  return i18n.t('words.dateAtTime', {
    date: datePart,
    time: timePart,
  });
};

export const formatLocaleDateShort = (
  date: Date | string | Dayjs,
  withTime: boolean = false
) => {
  let formatStr = localeDateShortFormat[resolveAppLanguage()];

  if (withTime) {
    formatStr += ' HH:mm';
  }

  return format(parseToDate(date), formatStr, { locale: getDateFnsLocale() });
};

export const formatToQueryDateTimeValue = (date: Date) => {
  return format(date, 'yyyy-MM-dd HH:mm:ss');
};

export const addSecondsToDate = (date: Date, seconds: number): Date => {
  return new Date(date.getTime() + seconds * 1000);
};

export const subSecondsToDate = (date: Date, seconds: number): Date => {
  return new Date(date.getTime() - seconds * 1000);
};

const toDate = (date: Date | Dayjs): Date =>
  'toDate' in date ? date.toDate() : date;

export const formatTimeRange = (
  from: Date | Dayjs,
  to: Date | Dayjs
): string => {
  const fromDate = toDate(from);
  const toDateValue = toDate(to);
  const locale = resolveAppLanguage();

  const formatTime = (date: Date) =>
    date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });

  return `${formatTime(fromDate)} - ${formatTime(toDateValue)}`;
};
